import { CSSProperties, MouseEvent, useState } from "react";
import { Alert, Box, Button, Divider, Grid, Input, InputLabel, Link, Typography, styled } from "@mui/material";
import { useAuth } from "@sal/authentication.react";
import { useNavigate, useSearchParams } from "react-router-dom";

const StyledInput = styled(Input)(() => ({
	color: "inherit",
	"&:not(.Mui-disabled):hover::before": {
		borderColor: "inherit"
	},
	"&:not(.Mui-disabled):before": {
		borderColor: "inherit"
	},
	"&:not(.Mui-disabled):after": {
		borderColor: "inherit"
	}
}));

type CustomFormLoginSlots = {
	hyperlinks?: React.ComponentType<Record<string, never>>;
};

export interface CustomFormLoginProps {
	inputLabelStyles?: CSSProperties;
	slots?: CustomFormLoginSlots;
}

const MICROSOFT_SSO_PATH = "/SingleSignOn/Microsoft";

function MoreSignInOptions() {
	const navigate = useNavigate();

	const onLoginWithMicrosoft = (event: MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		navigate(MICROSOFT_SSO_PATH);
	};

	return (
		<Box>
			<Box display="flex" alignItems="center">
				<Divider sx={{ flexGrow: 1 }} />
				<Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
					or
				</Typography>
				<Divider sx={{ flexGrow: 1 }} />
			</Box>
			<Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
				<Link href={MICROSOFT_SSO_PATH} variant="body1" underline="hover" onClick={onLoginWithMicrosoft}>
					Login with Microsoft
				</Link>
			</Box>
		</Box>
	);
}

function DefaultHyperlinksSlot() {
	return null;
}

export function CustomFormLogin({ inputLabelStyles, slots }: CustomFormLoginProps) {
	const auth = useAuth();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const [getUsername, setUsername] = useState("");
	const [getPassword, setPassword] = useState("");
	const [getMessage, setMessage] = useState("");

	const HyperlinksSlot = slots?.hyperlinks ?? DefaultHyperlinksSlot;

	const onChange = (setter: (value: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
		setMessage("");
		setter(event.target.value);
	};

	const validateControls = () => {
		if (getUsername.length === 0) {
			setMessage("Please enter a username.");
			return false;
		}
		if (getPassword.length === 0) {
			setMessage("Please enter a password.");
			return false;
		}
		return true;
	};

	const login_callback = (response: any) => {
		if (!response.success) {
			setMessage(response.message);
			return;
		}
		if (response.mfaRequired) {
			if (response.mfaSessionContactToken) {
				navigate((response.mfaRedirect || "/Mfa") + `/${response.mfaSessionContactToken}`);
				return;
			} else {
				setMessage(response.mfaRequiredMessage || "Setup missing.");
				return;
			}
		}
		navigate(searchParams.get("returnUrl") ?? "/");
	};

	const form_onsubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (validateControls()) {
			const remember = false;
			auth.login(getUsername, getPassword, remember, login_callback, null as any);
		}
	};

	return (
		<form onSubmit={form_onsubmit}>
			<Grid container justifyContent="center" alignItems="center" spacing={4}>
				<Grid item xs={12}>
					<Grid container direction="column" justifyContent="space-evenly">
						<Grid item>
							<InputLabel htmlFor="txtUsername" style={{ ...inputLabelStyles }}>
								Username
							</InputLabel>
						</Grid>
						<Grid item>
							<StyledInput
								fullWidth
								type="text"
								id="txtUsername"
								name="txtUsername"
								value={getUsername}
								placeholder="Enter username"
								autoComplete="off"
								autoCorrect="off"
								spellCheck="false"
								onChange={onChange(setUsername)}
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Grid container direction="column" justifyContent="space-evenly">
						<Grid item>
							<InputLabel htmlFor="txtPassword" style={{ ...inputLabelStyles }}>
								Password
							</InputLabel>
						</Grid>
						<Grid item>
							<StyledInput
								fullWidth
								name="txtPassword"
								id="txtPassword"
								type="password"
								value={getPassword}
								placeholder="Enter password"
								autoComplete="off"
								autoCorrect="off"
								spellCheck="false"
								onChange={onChange(setPassword)}
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Button type="submit" fullWidth variant="contained" color="primary" size="large">
						Log in
					</Button>
				</Grid>
				<Grid item xs={12}>
					<MoreSignInOptions />
				</Grid>
				<Grid item xs={12}>
					<HyperlinksSlot />
				</Grid>
				<Grid item xs={12}>
					{getMessage ? <Alert severity="error">{getMessage}</Alert> : null}
				</Grid>
			</Grid>
		</form>
	);
}
